import { CalendarClock, Loader2, MailX } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import React, { useEffect, useRef, useState } from 'react';

import { formatDate } from '@splitfiesta/core';
import {
  ACTUALLY_SUPPORTED_LANGUAGES,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';

import { StaticPageLayout } from '@/components/layout/StaticPageLayout';

import {
  useAppleRevocationMutation,
  useConfirmAccountDeletionMutation,
} from '@/hooks/queries/useUser';

type ConfirmState =
  | { kind: 'info' }
  | { kind: 'confirming' }
  | { kind: 'confirmed'; deletionEffectiveAt: string }
  // The Apple-return leg (?code&state) has no token/date to show — render
  // the confirmed card without the date sentence.
  | { kind: 'confirmed-external' }
  | { kind: 'invalid' }
  | { kind: 'error' };

type AppleStep =
  | { kind: 'hidden' }
  | { kind: 'offer'; clientId: string }
  | { kind: 'storing' }
  | { kind: 'done' }
  | { kind: 'failed' };

/** sessionStorage key carrying {token, nonce} across the Apple redirect. */
const SIWA_STORAGE_KEY = 'siwa-revocation';

/**
 * Apple authorize URL for the SIWA revocation re-auth. No scope on purpose:
 * requesting any scope forces response_mode=form_post, while scope-less
 * requests may use the query redirect this page consumes.
 */
export function buildAppleAuthorizeUrl(
  clientId: string,
  redirectUri: string,
  nonce: string
): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    state: nonce,
  });
  return `https://appleid.apple.com/auth/authorize?${params.toString()}`;
}

/**
 * PUBLIC /account/deletion page (store-compliance item 9).
 *
 * Two jobs in one URL — exactly what the deletion email links to:
 * - without ?token: the logged-out info page Google's Data-safety form
 *   requires (how to delete, what happens, contact fallback);
 * - with ?token: the confirmation landing for the emailed link — POSTs
 *   /user/deletion-confirm (no session; an expired token must never
 *   bounce the visitor to /login) and shows the scheduled date or the
 *   invalid-link state.
 */
export function AccountDeletionPage() {
  const { t, i18n } = useTranslation();
  // Cold external landings (Google Data-safety link, emailed link) hit this
  // page with no warmed i18n cache — hold rendering until the bundle lands,
  // like every sibling static page.
  const pagesLoaded = useNamespace('pages');
  // The consequence bullets reuse the settings-namespace keys the in-app
  // dialog renders — one source of truth, the page can't drift from it.
  const settingsLoaded = useNamespace('settings');

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // The email may be opened in any browser/locale — honor ?lang like the
  // invite landing page does.
  const langParam = searchParams.get('lang');
  // Validated once: also used for the confirm POST below, because
  // i18n.changeLanguage assigns its language only after the async bundle
  // load — reading i18n.language in the same mount commit would race and
  // send the pre-change value.
  const validatedLangParam =
    langParam &&
    ACTUALLY_SUPPORTED_LANGUAGES.includes(
      langParam as (typeof ACTUALLY_SUPPORTED_LANGUAGES)[number]
    )
      ? langParam
      : undefined;
  useEffect(() => {
    if (validatedLangParam && validatedLangParam !== i18n.language) {
      i18n.changeLanguage(validatedLangParam);
    }
  }, [validatedLangParam, i18n]);

  const confirmMutation = useConfirmAccountDeletionMutation();
  const appleRevocationMutation = useAppleRevocationMutation();

  // The Apple-return leg: the authorize redirect lands back here with
  // ?code&state on success, or ?error&state when the user cancels on
  // Apple's page (no ?token either way). Only meaningful when this browser
  // stashed {token, nonce} before leaving — a stray landing falls through
  // to info.
  const appleCode = searchParams.get('code');
  const appleError = searchParams.get('error');
  const appleState = searchParams.get('state');
  const appleReturn = Boolean((appleCode || appleError) && appleState);

  const [state, setState] = useState<ConfirmState>(() => {
    if (token) return { kind: 'confirming' };
    if (appleReturn && sessionStorage.getItem(SIWA_STORAGE_KEY)) {
      return { kind: 'confirmed-external' };
    }
    return { kind: 'info' };
  });
  const [appleStep, setAppleStep] = useState<AppleStep>({ kind: 'hidden' });

  // The server is the sole source of truth: confirm is idempotent for the
  // token's TTL, so a reload or duplicate tab replays as "confirmed" from
  // the server — and a link re-clicked after cancel (possibly from mobile
  // or another browser) truthfully fails as invalid instead of showing a
  // stale locally-cached "confirmed". The ref only dedupes StrictMode's
  // double-invoked effect.
  const confirmedRef = useRef(false);
  useEffect(() => {
    if (!token || confirmedRef.current) return;
    confirmedRef.current = true;

    confirmMutation
      .mutateAsync({ token, lang: validatedLangParam ?? i18n.language })
      .then(({ deletionEffectiveAt, appleRevocation }) => {
        setState({ kind: 'confirmed', deletionEffectiveAt });
        if (appleRevocation) {
          setAppleStep({ kind: 'offer', clientId: appleRevocation.clientId });
        }
      })
      .catch((err: { status?: number }) => {
        setState(err.status === 400 ? { kind: 'invalid' } : { kind: 'error' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Completes the Apple leg: verify the nonce, exchange the 5-minute code
  // server-side, then clear the stash. A cancel on Apple's page
  // (?error=user_cancelled_authorize) clears the stash too and reports the
  // neutral failed state. Failure is always neutral — the deletion stays
  // scheduled whether or not revocation was captured.
  const appleReturnRef = useRef(false);
  useEffect(() => {
    if (!appleReturn || token || appleReturnRef.current) return;
    appleReturnRef.current = true;

    const stashed = sessionStorage.getItem(SIWA_STORAGE_KEY);
    if (!stashed) return; // stray/cold landing — the info page renders
    sessionStorage.removeItem(SIWA_STORAGE_KEY);

    // Async runner so every setState happens in a callback, never
    // synchronously in the effect body (react-hooks/set-state-in-effect).
    void (async () => {
      if (!appleCode) {
        // User cancelled/errored on Apple's page — deletion is unaffected.
        setAppleStep({ kind: 'failed' });
        return;
      }

      let payload: { token?: string; nonce?: string } = {};
      try {
        payload = JSON.parse(stashed) as { token?: string; nonce?: string };
      } catch {
        // corrupt stash — treat as failed below
      }

      if (!payload.token || payload.nonce !== appleState) {
        setAppleStep({ kind: 'failed' });
        return;
      }

      setAppleStep({ kind: 'storing' });
      try {
        await appleRevocationMutation.mutateAsync({
          token: payload.token,
          code: appleCode,
        });
        setAppleStep({ kind: 'done' });
      } catch {
        setAppleStep({ kind: 'failed' });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appleReturn, appleCode, appleState, token]);

  const startAppleReauth = (clientId: string) => {
    if (!token) return;
    const nonce = crypto.randomUUID();
    sessionStorage.setItem(SIWA_STORAGE_KEY, JSON.stringify({ token, nonce }));
    window.location.href = buildAppleAuthorizeUrl(
      clientId,
      `${window.location.origin}/account/deletion`,
      nonce
    );
  };

  if (!pagesLoaded || !settingsLoaded) {
    return null;
  }

  return (
    <StaticPageLayout title={t('pages:accountDeletion.title')}>
      {state.kind === 'confirming' && (
        <div className="flex flex-col items-center py-12 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            {t('pages:accountDeletion.confirming')}
          </p>
        </div>
      )}

      {(state.kind === 'confirmed' || state.kind === 'confirmed-external') && (
        <div className="flex flex-col items-center py-12 text-center">
          <CalendarClock className="h-10 w-10 text-amber-600" />
          <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-50">
            {t('pages:accountDeletion.confirmedTitle')}
          </h2>
          {state.kind === 'confirmed' && (
            <p className="mt-3 max-w-xl text-gray-600 dark:text-gray-300">
              {t('pages:accountDeletion.confirmedBody', {
                // Local timezone on purpose — matches the mobile banner.
                date: formatDate(state.deletionEffectiveAt, i18n.language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
            </p>
          )}

          {/* SIWA revocation step (audit §1.2): offered only to
              Apple-federated accounts; skipping never affects the
              scheduled deletion. */}
          {appleStep.kind === 'offer' && (
            <div className="mt-8 w-full max-w-xl rounded-lg border border-gray-200 p-6 text-center dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {t('pages:accountDeletion.apple.title')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t('pages:accountDeletion.apple.body')}
              </p>
              <button
                type="button"
                onClick={() => startAppleReauth(appleStep.clientId)}
                className="mt-4 w-full rounded-lg bg-gray-900 px-4 py-2.5 font-medium text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                data-testid="apple-revocation-connect"
              >
                {t('pages:accountDeletion.apple.connect')}
              </button>
              <button
                type="button"
                onClick={() => setAppleStep({ kind: 'hidden' })}
                className="mt-3 text-sm text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                data-testid="apple-revocation-skip"
              >
                {t('pages:accountDeletion.apple.skip')}
              </button>
            </div>
          )}

          {appleStep.kind === 'storing' && (
            <div className="mt-8 flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('pages:accountDeletion.apple.storing')}</span>
            </div>
          )}

          {appleStep.kind === 'done' && (
            <div className="mt-8 w-full max-w-xl rounded-lg border border-gray-200 p-6 text-center dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                {t('pages:accountDeletion.apple.doneTitle')}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {t('pages:accountDeletion.apple.doneBody')}
              </p>
            </div>
          )}

          {appleStep.kind === 'failed' && (
            <p className="mt-8 max-w-xl text-sm text-gray-600 dark:text-gray-300">
              {t('pages:accountDeletion.apple.error')}
            </p>
          )}
        </div>
      )}

      {(state.kind === 'invalid' || state.kind === 'error') && (
        <div className="flex flex-col items-center py-12 text-center">
          <MailX className="h-10 w-10 text-error-500" />
          {state.kind === 'invalid' ? (
            <>
              <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-gray-50">
                {t('pages:accountDeletion.invalidTitle')}
              </h2>
              <p className="mt-3 max-w-xl text-gray-600 dark:text-gray-300">
                {t('pages:accountDeletion.invalidBody')}
              </p>
            </>
          ) : (
            <p className="mt-4 max-w-xl text-gray-600 dark:text-gray-300">
              {t('pages:accountDeletion.error')}
            </p>
          )}
        </div>
      )}

      {state.kind === 'info' && (
        <div className="space-y-6">
          <p className="text-gray-700 dark:text-gray-200">
            {t('pages:accountDeletion.intro')}
          </p>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('pages:accountDeletion.stepsTitle')}
            </h2>
            <ol className="list-decimal space-y-1 pl-5 text-gray-600 dark:text-gray-400">
              <li>{t('pages:accountDeletion.steps.one')}</li>
              <li>{t('pages:accountDeletion.steps.two')}</li>
              <li>{t('pages:accountDeletion.steps.three')}</li>
            </ol>
          </div>

          <div>
            <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">
              {t('pages:accountDeletion.whatTitle')}
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-gray-600 dark:text-gray-400">
              {(
                ['erased', 'comments', 'groups', 'history', 'grace'] as const
              ).map(key => (
                <li key={key}>{t(`settings:deleteAccount.bullets.${key}`)}</li>
              ))}
            </ul>
            <p className="mt-3 text-gray-600 dark:text-gray-400">
              {t('pages:accountDeletion.whatBody')}
            </p>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('pages:accountDeletion.noAccess')}
          </p>
        </div>
      )}
    </StaticPageLayout>
  );
}

export default AccountDeletionPage;
